import { NotificationData } from "@store/slices/notification";
import { FrontendAuthState, AuthErrorAction } from "./authStateDetection";

/**
 * Determine appropriate logout/auth error message based on context
 */
export const determineAuthErrorMessage = (
    authState: FrontendAuthState,
    action: AuthErrorAction,
    operationName?: string
): NotificationData => {
    
    // Handle different scenarios based on the action
    switch (action) {
        case 'logout':
            return createLogoutMessage(authState, operationName);
            
        case 'informational_redirect':
            return createInformationalMessage(authState, operationName);
            
        case 'silent_redirect':
            // For silent redirects, we typically don't show notifications
            // But if we need to, use a minimal message
            return createSilentRedirectMessage();
            
        default:
            return createDefaultAuthMessage();
    }
};

/**
 * Create logout message for users who were authenticated
 */
const createLogoutMessage = (
    authState: FrontendAuthState, 
    operationName?: string
): NotificationData => {
    
    // Special handling for checkout operations
    if (operationName?.toLowerCase().includes('checkout') || 
        operationName?.toLowerCase().includes('payment')) {
        return {
            type: 'warning',
            title: 'Sitzung abgelaufen',
            content: 'Ihre Sitzung ist während des Bezahlvorgangs abgelaufen. Bitte melden Sie sich erneut an, um Ihren Einkauf fortzusetzen.',
            autoHideDuration: 6000
        };
    }
    
    // Special handling for save/draft operations
    if (operationName?.toLowerCase().includes('save') || 
        operationName?.toLowerCase().includes('draft')) {
        return {
            type: 'warning',
            title: 'Sitzung abgelaufen',
            content: 'Deine Sitzung ist abgelaufen. Deine Änderungen wurden möglicherweise nicht gespeichert. Bitte melde Dich erneut an.',
            autoHideDuration: 5000
        };
    }
    
    // For users who were actively using the app
    if (authState.wasActivelyUsing) {
        return {
            type: 'info',
            title: 'Sitzung abgelaufen',
            content: 'Deine Sitzung ist abgelaufen. Du wurdest aus Sicherheitsgründen abgemeldet.',
            autoHideDuration: 4000
        };
    }
    
    // Default logout message
    return {
        type: 'info',
        title: 'Abgemeldet',
        content: 'Du wurdest aufgrund eines Authentifizierungsfehlers abgemeldet.',
        autoHideDuration: 3000
    };
};

/**
 * Create informational message for users who need to log in
 */
const createInformationalMessage = (
    authState: FrontendAuthState,
    operationName?: string
): NotificationData => {
    
    // If user had a token but it's invalid, this suggests session expiration
    if (authState.tokenExists) {
        return {
            type: 'warning',
            title: 'Sitzung abgelaufen',
            content: 'Deine Sitzung ist abgelaufen. Bitte melde Dich erneut an.',
            autoHideDuration: 4000
        };
    }
    
    // Special handling for protected operations
    if (operationName?.toLowerCase().includes('checkout')) {
        return {
            type: 'info',
            title: 'Anmeldung erforderlich',
            content: 'Bitte melde Dich an, um auf den Bezahlvorgang zuzugreifen.',
            autoHideDuration: 4000
        };
    }
    
    if (operationName?.toLowerCase().includes('profile') || 
        operationName?.toLowerCase().includes('user')) {
        return {
            type: 'info',
            title: 'Anmeldung erforderlich',
            content: 'Bitte melde Dich an, um auf Dein Profil zuzugreifen.',
            autoHideDuration: 3000
        };
    }
    
    // Default informational message
    return {
        type: 'info',
        title: 'Anmeldung erforderlich',
        content: 'Bitte melde Dich an, um fortzufahren.',
        autoHideDuration: 3000
    };
};

/**
 * Create minimal message for silent redirects (rarely used)
 */
const createSilentRedirectMessage = (): NotificationData => {
    return {
        type: 'info',
        title: 'Weiterleitung',
        content: 'Weiterleitung zur Anmeldung...',
        autoHideDuration: 2000
    };
};

/**
 * Default fallback message
 */
const createDefaultAuthMessage = (): NotificationData => {
    return {
        type: 'warning',
        title: 'Authentifizierungsfehler',
        content: 'Bitte melde Dich an, um fortzufahren.',
        autoHideDuration: 4000
    };
};

/**
 * Create custom message for specific scenarios
 */
export const createCustomAuthMessage = (
    type: 'warning' | 'info' | 'error',
    title: string,
    content: string,
    autoHideDuration: number = 4000
): NotificationData => {
    return {
        type,
        title,
        content,
        autoHideDuration
    };
};

/**
 * Get operation category for better message targeting
 */
export const getOperationCategory = (operationName?: string): string => {
    if (!operationName) return 'unknown';
    
    const name = operationName.toLowerCase();
    
    if (name.includes('checkout') || name.includes('payment') || name.includes('billing')) {
        return 'payment';
    }
    
    if (name.includes('save') || name.includes('draft') || name.includes('update')) {
        return 'save';
    }
    
    if (name.includes('profile') || name.includes('user') || name.includes('account')) {
        return 'profile';
    }
    
    if (name.includes('chat') || name.includes('message')) {
        return 'chat';
    }
    
    if (name.includes('company') || name.includes('subscription')) {
        return 'company';
    }
    
    return 'general';
};
